import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  FormField,
  getSkillConfig,
  SkillConfig,
} from '../../types/formSchema';

export interface FormFieldEditorProps {
  field: FormField;
  onUpdate: (updated: FormField) => void;
  onDelete: () => void;
  canEnableMoreVideos: boolean;
  onVideoLimitReached: () => void;
}

function toggleSkillPart(
  field: FormField,
  key: keyof Pick<SkillConfig, 'rating' | 'comment' | 'photo' | 'video'>,
  checked: boolean,
): FormField {
  const s = getSkillConfig(field);
  const next: SkillConfig = { ...s, [key]: checked };
  if (!next.rating && !next.comment && !next.photo && !next.video) {
    return field;
  }
  return {
    ...field,
    config: { skill: next },
  };
}

const FormFieldEditor: React.FC<FormFieldEditorProps> = ({
  field,
  onUpdate,
  onDelete,
  canEnableMoreVideos,
  onVideoLimitReached,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    position: 'relative',
    zIndex: isDragging ? 2 : 0,
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onUpdate({ ...field, label: e.target.value });
  };

  const handleSkillCheckbox =
    (key: keyof Pick<SkillConfig, 'rating' | 'comment' | 'photo' | 'video'>) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (key === 'video' && e.target.checked && !canEnableMoreVideos) {
        onVideoLimitReached();
        return;
      }
      onUpdate(toggleSkillPart(field, key, e.target.checked));
    };

  const skill = field.type === 'skill' ? getSkillConfig(field) : null;
  const fieldNeedsName = !field.label.trim();

  return (
    <div ref={setNodeRef} style={rowStyle}>
      <div
        className={`fe-field ${field.type === 'heading' ? 'fe-field-heading' : 'fe-field-skill'}`}
      >
        <div className="fe-field-row">
          <button
            type="button"
            className="fe-drag-handle"
            {...listeners}
            {...attributes}
            aria-label="Drag to reorder"
            title="Drag to reorder"
          >
            <span className="fe-drag-handle-bars" aria-hidden />
          </button>
          <div className="fe-field-inputs">
            <input
              className={
                [
                  field.type === 'heading'
                    ? 'fe-field-label-input fe-field-heading-input'
                    : 'fe-field-label-input',
                  fieldNeedsName ? 'fe-input-needs-name' : '',
                ].filter(Boolean).join(' ')
              }
              type="text"
              value={field.label}
              onChange={handleLabelChange}
              placeholder={
                field.type === 'heading'
                  ? 'Category (e.g. Standing, Running)...'
                  : 'Skill name...'
              }
              aria-label={field.type === 'heading' ? 'Category title' : 'Skill title'}
              aria-invalid={fieldNeedsName}
            />
            {field.type === 'skill' && skill ? (
              <div className="fe-skill-options">
                <label className="fe-skill-check">
                  <input
                    type="checkbox"
                    checked={skill.comment}
                    onChange={handleSkillCheckbox('comment')}
                  />
                  <span>Comment</span>
                </label>
                <label className="fe-skill-check">
                  <input
                    type="checkbox"
                    checked={skill.photo}
                    onChange={handleSkillCheckbox('photo')}
                  />
                  <span>Photo</span>
                </label>
                <label className="fe-skill-check">
                  <input
                    type="checkbox"
                    checked={skill.video}
                    onChange={handleSkillCheckbox('video')}
                  />
                  <span>Video</span>
                </label>
                <label className="fe-skill-check">
                  <input
                    type="checkbox"
                    checked={skill.rating}
                    onChange={handleSkillCheckbox('rating')}
                  />
                  <span>Rating</span>
                </label>
                {/* Skipping being able to change the max rating for now, just 5 always */}
                {/* {skill.rating ? (
                  <label className="fe-field-max-rating fe-skill-max">
                    <span className="fe-field-max-rating-label">Max:</span>
                    <select
                      className="fe-field-max-rating-select"
                      value={skill.maxRating}
                      onChange={handleMaxRatingChange}
                      aria-label="Maximum rating"
                    >
                      {MAX_RATING_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null} */}
              </div>
            ) : null}
          </div>
          <div className="fe-field-actions">
            <button
              type="button"
              className="fe-icon-btn fe-icon-btn-danger"
              onClick={onDelete}
              aria-label="Delete field"
              title="Delete field"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12.7 4.7l-1.4-1.4L8 6.6 4.7 3.3 3.3 4.7 6.6 8l-3.3 3.3 1.4 1.4L8 9.4l3.3 3.3 1.4-1.4L9.4 8l3.3-3.3z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormFieldEditor;
